'use client';

import { useSyncExternalStore } from 'react';
import { useTheme } from 'next-themes';
import { Moon, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  ThemeTileDark,
  ThemeTileLight,
  ThemeTileSystem
} from '@/components/shared/theme-mode-tile-art';
import { cn } from '@/lib/utils';
import type { ThemeChoice } from '@/lib/utils/theme';

const OPTIONS: ReadonlyArray<{
  value: ThemeChoice;
  label: string;
  Tile: typeof ThemeTileLight;
}> = [
  { value: 'light', label: 'Light', Tile: ThemeTileLight },
  { value: 'dark', label: 'Dark', Tile: ThemeTileDark },
  { value: 'system', label: 'System', Tile: ThemeTileSystem }
];

/** No-op subscribe for the hydration-safe `mounted` read below. */
const subscribeNoop = () => () => {};

/**
 * Light / dark / system theme picker (VALE-520 · DARK-3).
 *
 * Renders beside the Help button in the shared header-actions cluster, on every
 * authenticated page. Unconditional since the `dark-theme` flag retired
 * (VALE-871 · FLAG-6) — there is no longer an "off" rendering to fall back to.
 *
 * Sized to match `HelpCentreButton` exactly (`outline` / `xs`) so the two read
 * as one cluster rather than two unrelated controls that happen to be adjacent.
 * Icon-only: a theme control is understood by its glyph, and a second text label
 * next to "Help" would crowd the corner on a narrow header.
 *
 * **The menu is a row of preview tiles, not a list of words (VALE-612).** "Dark"
 * and "System" describe a result the user cannot picture — "System" especially,
 * to anyone who has not met the convention — so each option shows a thumbnail of
 * the app in that mode instead, the way mature desktop apps present it. The
 * artwork lives in `theme-mode-tile-art.tsx` and is deliberately hardcoded in
 * colour; the reason is load-bearing and documented there.
 *
 * **Deliberately dependency-free beyond `next-themes`, which is what lets it
 * mount in five headers and in Storybook without a provider stack.** Persisting
 * the choice to the user's profile (VALE-548) is `ThemePreferenceSync`'s job,
 * inside `ValeThemeProvider`, where the profile query provably exists — putting
 * a `useQuery` here instead made every suite that renders a page header require
 * a `QueryClientProvider`. `setTheme` is the seam: this is the only caller that
 * changes the CHOICE, so watching `theme` there is equivalent to watching this
 * handler, and it cannot be reached by an OS-preference change.
 */
export function ThemeToggle() {
  // `next-themes` resolves `theme` / `resolvedTheme` from `localStorage`, which
  // the server cannot see, so its first CLIENT render reads a different value
  // than the server rendered — this used to be masked by the `dark-theme` flag,
  // itself unresolved from `/profile` on both the server and the first client
  // render, so this component returned `null` identically either way with
  // nothing to mismatch. With the flag gone, this is the standard next-themes
  // fix: nothing renders until after mount. `useSyncExternalStore` (not a
  // `useEffect`-set boolean) so the flip happens on the very first client
  // render rather than a second, cascading one — same pattern as
  // `dev-feature-flag-toggle.tsx`.
  const mounted = useSyncExternalStore(
    subscribeNoop,
    () => true,
    () => false
  );

  const { theme, resolvedTheme, setTheme } = useTheme();

  if (!mounted) return null;

  const active: ThemeChoice = (theme as ThemeChoice | undefined) ?? 'system';
  // The glyph shows what is CURRENTLY RENDERED, not what was picked: `system`
  // has no glyph of its own, and a monitor icon on a dark screen tells the user
  // less than a moon does. Which of `dark` and `system` produced it is visible
  // in the menu, on the checked item.
  const TriggerIcon = resolvedTheme === 'dark' ? Moon : Sun;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button pad="sm" variant="outline" size="xs" aria-label="Change theme">
          <TriggerIcon className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-auto p-2">
        {/* Still the standard radio primitive, restyled — NOT a hand-rolled tile
            grid. It already carries the `menuitemradio` role, the checked state
            and roving-focus keyboard navigation, so turning the rows into tiles
            is a layout change that costs no accessibility: what a screen reader
            hears is unchanged from the text-row version this replaced. */}
        <DropdownMenuRadioGroup
          value={active}
          onValueChange={(value) => setTheme(value)}
          className="flex items-start gap-2"
        >
          {OPTIONS.map(({ value, label, Tile }) => (
            <DropdownMenuRadioItem
              key={value}
              value={value}
              // `pr-1` unwinds the row primitive's `pr-8` (the gutter it reserves
              // for a trailing tick), and the last-child rule hides that tick:
              // on a tile the ring IS the selected indicator, and a tick floating
              // over the artwork reads as a rendering defect. `aria-checked` is
              // untouched, so assistive tech still gets the state it conveyed.
              className={cn(
                'ring-offset-popover w-auto flex-col gap-1.5 rounded-md p-1 pr-1 ring-offset-2 [&>span:last-child]:hidden',
                value === active && 'ring-primary ring-2'
              )}
            >
              <Tile className="h-auto w-[88px] rounded-[4px]" />
              <span
                className={cn(
                  'w-full text-center text-xs',
                  value === active
                    ? 'text-foreground font-medium'
                    : 'text-muted-foreground'
                )}
              >
                {label}
              </span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
